import sleep from 'sleep-promise';

import { setLockProxyHost, getLockProxyHost } from '../constants/Meta';

import { env, authToken } from './settings.js';
import { robotLocktubePath } from './registry.js';
import { netLog, elasticLogException, elasticLogError, elasticLogEvent } from './ElasticLog.js';
import { fetchRetry, ajax } from './Network.js';
import { areSame } from './Utils.js';
import { getLocktubeIds, getRobotId } from './lib.js';
import { subscribeTubes } from './PollMgr';
import { getConfig } from '../utils/configUtils';

const READ_TUBES = '/read_tubes';
const WRITE_TUBES = '/write_tubes';
const GET_LOCK_TYPE = '/get_lock_type';

let polledLocktubeValues;

let loggedRobotTime = false;

export const LOCKTUBE = {
  loaded: 'loaded',
  unloaded: 'unloaded', // TODO: add more states in once we have the locktube location validation enabled
  unloading: 'unloading',
};

export const LOCKTUBE_ACCESS_STATUS = {
  available: 'available',
  requested: 'requested',
  filled: 'filled',
};

/**
 * Makes options object for use in calling into the robot's onboard tube proxy.
 *
 * @return {object} options as described above.
 */
function makeTubeProxyOptions(method, body) {
  let result = {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };

  if (body) result.body = JSON.stringify(body);

  return result;
}

export default class Locktube {
  /**
   * Tracks state of a robot locktube document
   * This class should be immutable after construction
   * TODO: This class should probably be renamed `Locktubes` or `LocktubeSet` since it tracks all locktubes on a robot
   * There are some global flags (badgeScanned, status) as well as per-locktube info in this.tubes
   * The set of tubes connected to a robot should be deduced by looking at keys of this.tubes (or getValidTubeIds)
   *
   * Flow should go something like the following:
   *   - Tubes start out empty with {access_status:'available', filled: false}
   *   - An in-progress task will use requestTubeSelectionToggle which will:
   *       set accessStatus = 'requested'
   *       set reservedBy = the current task guid
   *       set destination = the current task's destination
   *   - If a badge is scanned, robot will unlock requested tubes and set them to filled
   *   - Further calls to requestTubeSelectionToggle will check if tubes are filled:
   *         empty tubes will be reset to available and un-reserved
   *         filled tubes will be set to requested again, but left filled. They will never be set to empty until unload
   *
   * An example flow:
   *   Tubes start as {access_status:'available', filled: false}
   *   User selects a tube:
   *     requestTubeSelectionToggle sets a tube to {access_status: 'requested', filled: false}
   *   Badge scanned on robot and after tubes are opened and relocked:
   *     robot sets tube to {access_status: 'filled', filled:true}
   *   User decides to reopen the tube (in case nurse realizes they put the wrong thing in or forgot something)
   *     requestTubeSelectionToggle on a filled tube sets it to {access_status: 'requested', filled: true}
   *   User changes mind and no longer wants to open tube
   *     requestTubeSelectionToggle on a requested+filled tube resets it to {access_status: 'filled', filled: true}
   *
   * There are a number of invariants that we would like to maintain.
   * (e.g. Any tube with filled == True has a non-null destination and reservedBy)
   * There's also effectively permissions issues here where we don't want to allow accessing another delivery
   * TODO: I think it would be better to split up:
   *   - The requested state in a per-kiosk or per-task document
   *   - The physical state in a per-locktube document
   *   When badge is scanned, robot should use requested state to update the physical state
   */

  constructor(params) {
    /**
     * Initialize a Locktube object. Should be able to directly pass in firestore data
     */
    // Global flags for all locktubes
    this.arrivalMode = params.arrival_mode;
    this.badgeScanned = params.badge_scanned;
    this.scanResult = params.scan_result;
    this.lastScanTimestamp = params.last_scan_timestamp;
    this.arrivalDismissed = params.arrival_dismissed;
    this.rolloutCancelled = params.rollout_cancelled;
    this.status = params.status;
    this.robotTime = params.robot_time ? new Date(params.robot_time).getTime() : undefined;
    this.arrivalReadyForContinue = params.arrival_screen_ready_for_continue;
    this.socialCue = params.social_cue;
    this.tubes = {};
    this.taskAppState = params.task_app_state || {};
    this.readToUnlock = params.ready_to_unlock;
    this.readyToCancel = params.task_cancel_ready;

    for (const tubeId of getLocktubeIds()) {
      const tubeParams = params[tubeId];
      if (typeof tubeParams !== 'undefined') {
        this.tubes[tubeId] = {
          accessStatus: tubeParams.access_status,
          destination: tubeParams.destination,
          disabled: tubeParams.disabled,
          filled: tubeParams.filled,
          unlocked: tubeParams.unlocked,
          reservedBy: tubeParams.reserved_by,
        };
      }
    }

    Object.freeze(this);
  }

  static defaultLocktubes() {
    let params = {
      badge_scanned: false,
      status: LOCKTUBE.unloaded,
      // arrival_screen_ready_for_continue:true
    };
    for (const tubeId of getLocktubeIds()) {
      params[tubeId] = {
        access_status: LOCKTUBE_ACCESS_STATUS.available,
        destination: '',
        filled: false,
        unlocked: false,
      };
    }
    return new Locktube(params);
  }

  getValidTubeIds = () => {
    return Object.keys(this.tubes);
  };

  anyTubeUnlocked = () => {
    for (const tubeId of Object.keys(this.tubes)) {
      if (!this.tubes[tubeId].disabled && this.tubes[tubeId].unlocked) {
        return true;
      }
    }
    return false;
  };

  /**
   * Extract accessStatus field from each defined locktube
   * returns {dict} - mapping from values in getLocktubeIds() to values in LOCKTUBE_ACCESS_STATUS
   */
  getAccessStatuses = () => {
    let result = {};
    for (const tubeId of getLocktubeIds()) {
      const tubeData = this[tubeId];
      if (typeof tubeData !== 'undefined') {
        result[tubeId] = tubeData.accessStatus;
      }
    }
  };


  /**
   * Request that the robot change the state of a locktube to "requested" or not
   * This doesn't modify the locktube object directly. Will need to use Locktube.onUpdate to see these changes
   * This assumes the current locktube state is accurately synced with the robot
   * @param {str} robotId - id of the tracked robot
   * @param {str} tubeId - id of the tube that will be updated
   * @param {str} destinationId - destination that should be delivered to (only used when setting to requested)
   * @param {str} taskId - uuid of task that should be associated with this delivery
   * @return {str or null} - A description of why tube can't be toggled, or undefined if request was sent
   */
  requestTubeSelectionToggle = (tubeId, destinationId, taskId) => {
    let robotId = getRobotId();
    if (!robotId) throw new Error('Invalid robot id');  //we're on the bot and it's a local call, so fail loudly

    if (this.anyTubeUnlocked()) return "Can't toggle selection while unlocked";

    const currentTube = this.tubes[tubeId];
    if (!currentTube) return 'Invalid locktube id: ' + tubeId;
    if (currentTube.disabled) return "Can't request a disabled drawer";


    if (currentTube.accessStatus == LOCKTUBE_ACCESS_STATUS.requested) {
      // Was requested, need to check if it is full or not
      if (currentTube.filled) {
        // This was already filled. Reset access status
        if (getConfig().userFacingBatching && !destinationId)
          Locktube._unsetTubeDestination(robotId, tubeId);

        Locktube._unsetTubeRequested(robotId, tubeId);
      }
      else {
        Locktube._setTubeAvailable(robotId, tubeId);
      }
    }
    else if (currentTube.accessStatus == LOCKTUBE_ACCESS_STATUS.available) {
      Locktube._setTubeRequested(robotId, tubeId, destinationId, taskId);
    }
    else if (currentTube.accessStatus == LOCKTUBE_ACCESS_STATUS.filled) {
      if (currentTube.reservedBy !== taskId) {
        // Someone else sent this tube. Don't allow requesting it
        return "Can't request to use tube from another sender";
      }
      else {
        // Was previously filled, set back to requested
        Locktube._setTubeRequested(robotId, tubeId, destinationId, taskId);
      }
    }
    else {
      return "ERROR: Can't update tube " + tubeId + ' because state is unknown: ' + currentTube.accessStatus;
    }

    //no error
  };

  //set_sim_baseline is a mechanism to let the unload listener know
  // when the badge_scanned field can be paid attention to.
  static fakeBadgeScan(robotId, badge_scanned, callback = null, set_sim_baseline = false) {
    let update = { badge_scanned };
    if (set_sim_baseline) update['sim_baseline'] = true;
    Locktube._updateLocktubeDoc(robotId, update, callback);
  }

  /**
   * Internal function to write to the locktube document
   * @param {str} robotId - id of the tracked robot
   * @param {dict} delta - properties that should change
   * @param {function} callback - no-arg function to call when update finishes, or undefined.
   */
  static async _updateLocktubeDoc(robotId, delta, callback = undefined) {
    let start = Date.now();
    let elapsedSecs = () => Math.floor((Date.now() - start) / 100) / 10; //seconds in single-decimal precision
    try {
      let response;

      elasticLogEvent({ what: 'updateLocktubeDoc-start', delta });

      console.log('updating locktubes with delta: ' + JSON.stringify(delta, undefined, 2));
      if (env.firestore.isSim) delta = { ...delta, robotId };
      try {
        response = await fetchRetry(
          'updateLocktubeDoc',
          getLockProxyHost() + WRITE_TUBES,
          makeTubeProxyOptions('POST', delta),
        );
        if (response && (!response.ok || response.status != 200)) {
          elasticLogError({
            what: 'updateLocktubeDoc-err',
            ok: response.ok,
            status: response.status,
            delta,
            secs: elapsedSecs(),
          });
        }
        else {
          elasticLogEvent({ what: 'updateLocktubeDoc-end', delta, secs: elapsedSecs() });
        }
      }
      catch (err) {
        elasticLogError({ what: 'updateLocktubeDoc-catch1', message: '' + err, delta, secs: elapsedSecs() });
      }

      if (callback) callback();
    }
    catch (error) {
      elasticLogException({ ctx: 'updateLocktubeDoc-catch2', message: '' + error, delta, secs: elapsedSecs() });
    }
  }

  /**
   * Waits for the specified status, timing out after the specified number of seconds.
   *
   * @param {string} status to wait for.
   * @param {int} maxSeconds the max number of seconds to wait.
   */
  static async waitForStatus(status, maxSeconds) {
    let endTicks = Date.now() + 1000 * maxSeconds;
    do {
      if (polledLocktubeValues && polledLocktubeValues.status == status) {
        console.log('waitForStatus got status: ' + status);
        return; //success
      }
      await sleep(250);
    } while (Date.now() < endTicks);
    elasticLogError({ what: 'Locktube.waitForStatus', msg: 'Timed out', status });
  }

  /**
   * Updates the overall status flag of the locktube document
   * TODO: Refactor to not pass a response to callback
   * TODO: Rename this setOverallStatus
   * @param {str} robotId - id of the tracked robot
   * @param {str} status - new status flag value
   * @param {function} callback - no-arg function to call when update finishes, or undefined.
   */
  static updateLocktubeStatus(robotId, status, callback = null) {
    let delta = {
      status: status,
    };
    return Locktube._updateLocktubeDoc(robotId, delta, callback);
  }

  /**
   * Cancels the rollout from Unload phase.
   * @param {str} robotId - id of the robot
   */
  static cancelRollout(robotId) {
    let delta = {
      rollout_cancelled: true,
    };
    return Locktube._updateLocktubeDoc(robotId, delta);
  }

  /**
   * Tells the robot that the user has canceled the task after loading drawer(s) that now need to be unloaded.
   * @param {str} robotId - id of the robot
   */
  static taskCancelFromLoadScreen(robotId) {
    let delta = {
      task_cancel_after_load: true,
    };
    return Locktube._updateLocktubeDoc(robotId, delta);
  }

  /**
   * Signals that the arrival screen has been tapped.
   * @param {str} robotId - id of the robot
   */
  static signalArrivalTapped(robotId) {
    let delta = {
      arrival_screen_tapped: true,
    };
    return Locktube._updateLocktubeDoc(robotId, delta);
  }

  static _setTubeRequested(robotId, tubeId, destination, task_id) {
    let delta = {};
    delta[[tubeId, 'access_status'].join('.')] = LOCKTUBE_ACCESS_STATUS.requested;
    delta[[tubeId, 'destination'].join('.')] = destination;
    delta[[tubeId, 'reserved_by'].join('.')] = task_id;
    // TODO: Ideally we would enforce these conditions (on server or by using read/verify/write in a transaction):
    //   tube.access_status == 'available'
    //   tube.destination == ''
    //   tube.locked == true
    //   tube.filled == false
    Locktube._updateLocktubeDoc(robotId, delta);
  }

  /* * Reset access_status that was requested back to filled
   * This should only be used on a tube that was filled before when user decides they don't want to reopen the tube
   * @param {str} robotId - id of the tracked robot
   * @param {str} tubeId - id of the tube to change
   */
  static _unsetTubeRequested(robotId, tubeId) {
    let delta = {};
    delta[[tubeId, 'access_status'].join('.')] = LOCKTUBE_ACCESS_STATUS.filled;
    // TODO: Ideally we would enforce these conditions (on server or by using read/verify/write in a transaction):
    //   tube.access_status == 'requested'
    //   tube.destination != ''
    //   tube.locked == true
    //   tube.filled == true
    Locktube._updateLocktubeDoc(robotId, delta);
  }

  /**
   * Reset access_status from requested (but with filled == false) to available
   * This should only be used on a tube that was filled before when user decides they don't want to reopen the tube
   * @param {str} robotId - id of the tracked robot
   * @param {str} tubeId - id of the tube to change
   */
  static _setTubeAvailable(robotId, tubeId) {
    let delta = {};
    delta[[tubeId, 'access_status'].join('.')] = LOCKTUBE_ACCESS_STATUS.available;
    delta[[tubeId, 'destination'].join('.')] = '';
    delta[[tubeId, 'reserved_by'].join('.')] = ''; //firebase.firestore.FieldValue.delete();

    // TODO: Ideally we would enforce these conditions (on server or by using read/verify/write in a transaction):
    //   tube.access_status == 'requested'
    //   tube.locked == true
    //   tube.filled == false
    Locktube._updateLocktubeDoc(robotId, delta);
  }

  /**
   * Clear the destination and reset access_status from filled to available (while filled still true)
   * This should only be used on a tube that was filled before when user decides they don't want to reopen the tube
   * Added for user facing batches (BART-669)
   * @param {str} robotId - id of the tracked robot
   * @param {str} tubeId - id of the tube to change
   */
  static _unsetTubeDestination(robotId, tubeId) {
    let delta = {};
    delta[[tubeId, 'access_status'].join('.')] = LOCKTUBE_ACCESS_STATUS.available;
    delta[[tubeId, 'destination'].join('.')] = '';

    Locktube._updateLocktubeDoc(robotId, delta);
  }

  /**
   * The object returned by this routine enables UI informing operators that the
   * tube proxy is reachable, thereby short-circuiting operator errors that
   * might involve not having the on-board robot ipad networked into Moxi's
   * onboard access point, or revealing any problem that would prevent the
   * communication from happening (wrong robot sw version, robot stack
   * down/crashed, etc).
   *
   * @return an object which will, for its lifetime, repeatedly attempt to
   *    verify that it can reach the robot tube proxy; calling getVerified() on
   *    the object will return true IFF the proxy has been reached at least once
   *    since the creation of the object; the object's finish() method must be
   *    called when done with the object.
   */
  static getTubeProxyVerifier() {
    let hosts = [getConfig().lockProxyHost, getConfig().lockProxyHostEncrypted]
      .filter((h) => h)
      .map((host) => ({ host, pollPromise: undefined }));
    let result = {
      verified: false, //!TUBE_PROXY,  //the object's functionality is basically a no-op when TUBE_PROXY is false
      timeout: undefined,
      stopped: false,
      lockType: undefined,
      lockNames: undefined,

      _check: async () => {
        if (result.timeout) {
          clearTimeout(result.timeout);
          result.timeout = undefined;
        }
        if (result.stopped) return;

        hosts.forEach((hostData, i) => {
          hostData.pollPromise =
            hostData.pollPromise ||
            new Promise(async (resolve, reject) => {
              try {
                const exactlyOneAttempt = 1; //no retries, because we'll be polling anyway
                let response = await fetchRetry(
                  'getLockType',
                  hostData.host + GET_LOCK_TYPE,
                  makeTubeProxyOptions('GET'),
                  exactlyOneAttempt,
                  1000,
                  false,
                );
                if (response && response.ok && response.status == 200) {
                  let json = await response.json();
                  if (json.LOCKTYPE) {
                    result.lockType = json.LOCKTYPE;
                    result.lockNames = json.LOCKNAMES;
                    result.verified = true;

                    result.stop();

                    setLockProxyHost(hostData.host);

                    return; //latched, no need to check further
                  }
                }
                else {
                  console.log('getTubeProxyVerifier: bad result trying to reach tube proxy, status=' + response.status);
                }
              }
              catch (err) {
                console.log('getTubeProxyVerifier: error trying to reach tube proxy: ' + err);
              }
              finally {
                hostData.pollPromise = undefined;
                resolve();
              }
            });
        });

        result.timeout = setTimeout(() => result._check(), 1000);
      },

      getVerified: () => result.verified,

      getLockType: () => {
        return { lockType: result.lockType, lockNames: result.lockNames };
      },

      stop: () => {
        result.stopped = true;
        if (result.timeout) {
          clearTimeout(result.timeout);
          result.timeout = undefined;
        }
      },
    };

    result._check(); //start checking loop

    return result;
  }

  /**
   * Calls passed in callback function when the locktube state document updates
   *
   * @param {str} robotId - id of the tracked robot
   * @param {function} callback - function to call when locktube state updates, takes updated Locktube as input
   *
   * @return {function} - unsubscribes from updates, takes no inputs
   *
   * TODO: verify that unsubscribes are happening in client code.
   */
  static onUpdate(robotId, callback) {
    // check for invalid robotId
    if (!robotId) {
      console.log("RobotId not yet set. Can't initialize locktube listener");
      return () => {};
    }

    function wrapped_callback(doc) {
      let docData = doc.data();
      if (docData) {
        let locktubes = new Locktube(docData);
        callback(locktubes);
      }
    }

    return subscribeTubes((json) => {
      if (!loggedRobotTime && typeof json.robot_time == 'string') {
        loggedRobotTime = true;
        let now = new Date();
        let robotTimeDiff = now - new Date(json.robot_time).getTime();
        elasticLogEvent({ what: 'robotTimeDiff', robotTimeDiff, msg: `TimeDiff is ${robotTimeDiff}` });
      }

      // delete json.robot_time; //because it'll differ every poll, masking other differences if undeleted

      polledLocktubeValues = json;
      console.log('Locktube data changed', json);
      elasticLogEvent({ what: 'tubePollDataChange', json });
      wrapped_callback({ data: () => json });
    });
  }
}
